import { UserAttributes } from '@ohpen/react-utils';

import { AppMenuItem, GuardedItem, MenuItemGuard } from '@pmp/components';
import { checkFlags, checkPermissions } from '@ohpen/react-authz';
import { FeatureFlag, GuardOptions, Permission, Resource, Role, RoleGroup, RoleGroupView, SignInMethod } from './interfaces';
import { PermissionAction, PermissionResource } from '@pmp/api';

export const PERMISSIONS = {
  lenders: ['view', 'create', 'update', 'delete', 'review'],
  firms: ['view', 'create', 'update', 'delete', 'review'],
  brokers: ['view', 'create', 'update', 'delete', 'review'],
  procurationFees: ['view', 'create', 'update', 'delete', 'review'],
  brokerPortalCases: ['view', 'update', 'create'],

  products: ['view', 'create', 'update', 'review'],
  baseRates: ['view', 'create', 'update', 'delete', 'review'],
  exchangeRates: ['view', 'create', 'update', 'delete', 'review'],
  ercProfiles: ['view', 'create', 'update', 'delete', 'review'],
  fees: ['view', 'create', 'update', 'delete', 'review'],
  feeProfiles: ['view', 'create', 'update', 'delete', 'review'],
  exclusivitySets: ['view', 'create', 'update', 'delete', 'review'],

  imports: ['view', 'create', 'update', 'delete'],
  exportTemplates: ['view', 'create', 'update', 'delete'],
  changeRequests: ['view', 'self_review'],

  configUsers: ['view'],
  configRoles: ['view'],
  configPermissions: ['view'],
  configFeatureFlags: ['view'],
  configParameters: ['view'],
  configValueLists: ['view'],
  configTranslations: ['view'],
  configCustomFields: ['view'],
  configAuditLog: ['view'],
} as const satisfies Record<string, readonly string[]>;

export const isPermission = (value: string): value is Permission => {
  const i = value.indexOf(':');
  if (i < 0) return false;
  const resource = value.slice(0, i);
  if (!Object.prototype.hasOwnProperty.call(PERMISSIONS, resource)) return false;
  const actions: readonly string[] = PERMISSIONS[resource as Resource];
  return actions.includes(value.slice(i + 1));
};

const RESOURCE_BY_API_KEY: Partial<Record<PermissionResource, Resource>> = {
  [PermissionResource.PRODUCTS]: 'products',
  [PermissionResource.BASE_RATES]: 'baseRates',
  [PermissionResource.EXCHANGE_RATES]: 'exchangeRates',
  [PermissionResource.ERC_PROFILES]: 'ercProfiles',
  [PermissionResource.FEES]: 'fees',
  [PermissionResource.FEE_PROFILES]: 'feeProfiles',
  [PermissionResource.EXCLUSIVITY_SETS]: 'exclusivitySets',
  [PermissionResource.IMPORT]: 'imports',
  [PermissionResource.EXPORT_TEMPLATES]: 'exportTemplates',
  [PermissionResource.CHANGE_REQUESTS]: 'changeRequests',
  [PermissionResource.LENDER]: 'lenders',
  [PermissionResource.FIRMS]: 'firms',
  [PermissionResource.INTERMEDIARIES]: 'brokers',
  [PermissionResource.PROCURATION_FEES]: 'procurationFees',
  [PermissionResource.BROKER_PORTAL_CASES]: 'brokerPortalCases',
  [PermissionResource.CONFIG_USERS]: 'configUsers',
  [PermissionResource.CONFIG_ROLES]: 'configRoles',
  [PermissionResource.CONFIG_PERMISSIONS]: 'configPermissions',
  [PermissionResource.CONFIG_FEATURE_FLAGS]: 'configFeatureFlags',
  [PermissionResource.CONFIG_PARAMETERS]: 'configParameters',
  [PermissionResource.CONFIG_VALUE_LISTS]: 'configValueLists',
  [PermissionResource.CONFIG_TRANSLATIONS]: 'configTranslations',
  [PermissionResource.CONFIG_CUSTOM_FIELDS]: 'configCustomFields',
  [PermissionResource.CONFIG_AUDIT_LOG]: 'configAuditLog',
};

export const toPermissionSet = (apiPermissions?: Record<string, PermissionAction[]>): Set<Permission> => {
  const result = new Set<Permission>();
  for (const [apiKey, actions] of Object.entries(apiPermissions ?? {})) {
    const resource = RESOURCE_BY_API_KEY[apiKey as PermissionResource];
    if (!resource) continue;
    for (const action of actions ?? []) {
      // The API may grant actions this application does not declare; they are kept so role catalogues show them.
      result.add(`${resource}:${action.toLowerCase()}` as Permission);
    }
  }
  return result;
};

/**
 * Data-driven role catalogue. Each role maps to its display group plus an i18n key for
 * its (optional) description. Adding a new role — e.g. a future lender sub-role — is just
 * a new entry here; the profile UI needs no changes. The role name itself is shown as the
 * tag label, so no separate label mapping is kept. `description` is an i18n key.
 */
export const ROLE_DEFINITIONS: Record<Role, { group: RoleGroup; description: string; deprecated?: boolean }> = {
  PMP_Update: { group: 'products', description: 'profile.roleDescriptions.productsEditor' },
  PMP_ReadOnly: { group: 'products', description: 'profile.roleDescriptions.productsViewer' },
  PMP_LenderAdmin: { group: 'lender', description: 'profile.roleDescriptions.lenderAdmin' },
  PMP_LenderViewer: { group: 'lender', description: 'profile.roleDescriptions.lenderViewer' },
  PMP_LenderSupport: { group: 'lender', description: 'profile.roleDescriptions.lenderSupport' },
  PMP_Lenders: { group: 'lender', description: 'profile.roleDescriptions.lenderLegacy', deprecated: true },
  PMP_Reviewers: { group: 'review', description: 'profile.roleDescriptions.changesReviewer' },
  PMP_SelfReviewer: { group: 'review', description: 'profile.roleDescriptions.selfReviewer' },
  PMP_ConfigReadOnly: { group: 'config', description: 'profile.roleDescriptions.configReadOnly' },
  PMP_ConfigUpdate: { group: 'config', description: 'profile.roleDescriptions.configUpdate' },
  PMP_ConfigAdmin: { group: 'config', description: 'profile.roleDescriptions.configAdmin' },
};

const GROUP_ORDER: RoleGroup[] = ['products', 'lender', 'review', 'config'];

const GROUP_LABELS: Record<RoleGroup, string> = {
  products: 'profile.groups.products',
  lender: 'profile.groups.lender',
  review: 'profile.groups.review',
  config: 'profile.groups.config',
};

const ROLE_ORDER = Object.keys(ROLE_DEFINITIONS) as Role[];

/**
 * Resolve every role the user holds into display groups, preserving group and
 * within-group order. Unknown roles and empty groups are omitted. Unlike the old
 * behaviour, no role is hidden by a "highest of the group" collapse — all are shown.
 */
export const getGroupedRoles = (roles: Role[]): RoleGroupView[] => {
  const roleSet = new Set(roles);
  return GROUP_ORDER.map(group => ({
    group,
    groupLabel: GROUP_LABELS[group],
    roles: ROLE_ORDER.filter(role => roleSet.has(role) && ROLE_DEFINITIONS[role].group === group).map(role => {
      const def = ROLE_DEFINITIONS[role];
      return { role, description: def.description, deprecated: def.deprecated };
    }),
  })).filter(view => view.roles.length > 0);
};

/**
 * Derive how the user signed in from the Cognito `identities` attribute. Federated
 * (SSO) users carry a JSON array of identity objects with a `providerName`; native
 * credential users have no `identities` claim.
 */
export const getSignInMethod = (attributes?: UserAttributes | null): SignInMethod => {
  const identities = attributes?.identities;
  if (!identities) return { type: 'credentials' };

  try {
    const parsed = JSON.parse(identities);
    const list = Array.isArray(parsed) ? parsed : [parsed];
    if (!list.length) return { type: 'credentials' };
    const provider = typeof list[0]?.providerName === 'string' ? list[0].providerName : undefined;
    return { type: 'sso', provider };
  } catch {
    // Present but not JSON — still a federated identity, provider unknown.
    return { type: 'sso' };
  }
};

export const getUserGroups = (groups?: string) => {
  if (!groups) return [];

  // Try to parse as JSON (array or quoted string)
  // Cognito stores single groups as JSON strings e.g. "PMP_Lenders"
  // and multiple groups as JSON arrays e.g. ["PMP_Update","PMP_Reviewers"]
  if (groups.startsWith('[') || groups.startsWith('"')) {
    try {
      const parsed = JSON.parse(groups);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === 'string') return [parsed];
    } catch {
      if (groups.startsWith('[')) {
        // Legacy format: [Group1, Group2] (without quotes inside brackets)
        return groups.slice(1, -1).split(', ');
      }
    }
  }

  // Plain string (no JSON encoding)
  return [groups];
};

const passesGuard = (
  guard: MenuItemGuard | undefined,
  permissionsSet: Set<Permission>,
  flagsSet: Set<FeatureFlag>,
  { permissionsOperator = 'OR', flagsOperator = 'OR' }: GuardOptions,
) => {
  if (!guard) return true;

  const permsOk = checkPermissions({
    permissions: guard.permissions,
    userPermissions: permissionsSet,
    permissionsOperator,
  });

  const flagsOk = checkFlags({
    flags: guard.flags,
    envFeatureFlags: flagsSet,
    flagsOperator,
  });

  return permsOk && flagsOk;
};

const pruneDividers = (items: AppMenuItem[]) => {
  const out: AppMenuItem[] = [];
  for (const it of items) {
    if (it?.type === 'divider') {
      if (!out.length) continue;
      if (out[out.length - 1]?.type === 'divider') continue;
    }
    out.push(it);
  }
  if (out[out.length - 1]?.type === 'divider') out.pop();
  return out;
};

export function filterMenuByGuard(
  items: GuardedItem[],
  userPermissions: Set<Permission>,
  envFeatureFlags: Set<FeatureFlag>,
  options: GuardOptions = {},
): AppMenuItem[] {
  const mapOne = (item: GuardedItem): AppMenuItem | null => {
    if (!passesGuard(item.guard, userPermissions, envFeatureFlags, options)) return null;

    const { guard, children, ...rest } = item as any;
    if (!children) return rest as AppMenuItem;

    const visibleChildren = (children as GuardedItem[]).map(mapOne).filter(Boolean);
    if (!visibleChildren.length) return null;

    return { ...rest, children: visibleChildren } as AppMenuItem;
  };

  const filtered = items.map(mapOne).filter(Boolean);
  return pruneDividers(filtered);
}
